/**
 * `ExternalCvJobDetail` — pagina di dettaglio del posizionamento su una
 * professione (Job e-CF 3.0) nell'ambito di un profilo CV esterno.
 *
 * Rotta `/external-cv/:id/jobs/:jobId` (protetta lato API da
 * `requireLevelsAccess`; la guardia di rotta `LevelsRoute` è difensiva).
 *
 * Carica il posizionamento del profilo sulla professione da
 * `GET /api/external-cv/profiles/:id/job-positioning/:jobId` → `JobMatch` e ne
 * delega la resa al componente presentazionale `JobPositioningView` (lo stesso
 * riusato da `JobDetail`). Qui restano solo la fonte dati, gli stati di
 * caricamento/errore e il calcolo di `backTo`/`competenceDetailPath` nel
 * contesto del profilo esterno: il "torna indietro" riporta al profilo
 * (`/external-cv/:id`) e i link alle competenze puntano al dettaglio competenza
 * del profilo esterno (`/external-cv/:id/competences/:competenceId`).
 *
 * Il `404` (professione inesistente) mostra un empty state dedicato; il `403`
 * (accesso negato) è trattato come tale (guardia difensiva: l'API resta
 * l'autorità autorevole sull'autorizzazione). Nessuno dei due va ritentato.
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { type JobMatch } from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ApiRequestError } from '../api/client';
import { JobPositioningView } from '../components/JobPositioningView';
import { Button, EmptyState, Loading } from '../components/ui';

export default function ExternalCvJobDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { id, jobId } = useParams<{ id: string; jobId: string }>();

  const jobQuery = useQuery<JobMatch>({
    queryKey: ['externalCv', 'profiles', id, 'job-positioning', jobId],
    queryFn: () =>
      api.get<JobMatch>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/job-positioning/${encodeURIComponent(jobId ?? '')}`,
      ),
    enabled: Boolean(id) && Boolean(jobId),
    // Una professione inesistente (404) o un accesso negato (403) non vanno
    // ritentati.
    retry: false,
  });

  const job = jobQuery.data ?? null;

  const backToProfile = () => {
    void navigate(`/external-cv/${encodeURIComponent(id ?? '')}`);
  };

  const backLabel = '← Torna al profilo';

  const competenceDetailPath = (competenceId: string) =>
    `/external-cv/${encodeURIComponent(id ?? '')}/competences/${encodeURIComponent(competenceId)}`;

  // ----- Rendering -----

  if (jobQuery.isLoading) {
    return (
      <section aria-labelledby="job-detail-title">
        <h1 id="job-detail-title">Dettaglio professione</h1>
        <Loading>Caricamento della professione…</Loading>
      </section>
    );
  }

  if (jobQuery.isError || job === null) {
    const error = jobQuery.error;
    const notFound = error instanceof ApiRequestError && error.kind === 'not_found';
    // Il `403` è trattato come accesso negato (guardia difensiva; l'API è
    // l'autorità autorevole sull'autorizzazione).
    const forbidden = error instanceof ApiRequestError && error.kind === 'forbidden';
    const message = forbidden
      ? 'Non hai i permessi per consultare questa professione.'
      : notFound
        ? 'La professione richiesta non esiste o non è più disponibile.'
        : 'La professione richiesta non esiste o non è più disponibile.';
    return (
      <section aria-labelledby="job-detail-title">
        <h1 id="job-detail-title">Dettaglio professione</h1>
        <EmptyState role="status">{message}</EmptyState>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToProfile}>
            {backLabel}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <JobPositioningView
      job={job}
      backTo={{ label: backLabel, onClick: backToProfile }}
      competenceDetailPath={competenceDetailPath}
      subtitle="Le competenze richieste dalla professione e il posizionamento del candidato."
    />
  );
}
